import "./index.css";
import { clsx } from "clsx";
import type {
  FocusEvent,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
  TouchEvent,
} from "react";
import { isValidElement, useCallback, useId, useMemo, useState } from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { useLongPress } from "../../hooks/useLongPress";
import { isTouchDevice, Slot } from "../../utils";
import type { DropdownInteraction } from "../Dropdown";
import { Dropdown } from "../Dropdown";
import type { ITooltipProps } from "./types";

export const Tooltip = (props: ITooltipProps) => {
  const {
    trigger,
    children,
    content,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    placement = "top",
    offset = 8,
    maxWidth = 400,
    contentClassName,
    hoverOptions,
    ...forwardedProps
  } = props;

  const isMobile = useMemo(isTouchDevice, []);
  const [wasClicked, setWasClicked] = useState(false);

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen || false,
  });

  const longPress = useLongPress({
    delay:
      typeof hoverOptions?.delay === "number"
        ? hoverOptions?.delay
        : typeof hoverOptions?.delay === "function"
          ? 500
          : hoverOptions?.delay?.open || 500,
    onLongPress: () => {
      onOpenChange(true);
    },
  });

  const generatedId = useId();
  const triggerId = children.props.id || `${generatedId}-trigger`;
  const contentId =
    isValidElement(content) &&
    (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      ? (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      : `${generatedId}-content`;

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setWasClicked(true);
      onOpenChange(false);
    },
    [onOpenChange],
  );

  const handleBlur = useCallback((_e: FocusEvent) => {
    setWasClicked(false);
  }, []);

  const handleMouseLeave = useCallback((_e: MouseEvent) => {
    setWasClicked(false);
  }, []);

  const triggerElementBase = trigger || children;

  const mobileHandlers = useMemo(
    () =>
      isMobile
        ? {
            onTouchStart: (_e: TouchEvent) => {
              longPress.onTouchStart();
            },
            onTouchEnd: (e: TouchEvent) => {
              longPress.onTouchEnd(e);
            },
            onTouchCancel: (_e: TouchEvent) => {
              longPress.onTouchCancel();
            },
          }
        : {},
    [isMobile, longPress],
  );

  const triggerElement = useMemo(
    () => (
      <Slot
        {...forwardedProps}
        onClick={handleClick}
        onBlur={handleBlur}
        onMouseLeave={handleMouseLeave}
        {...mobileHandlers}
      >
        {triggerElementBase}
      </Slot>
    ),
    [
      forwardedProps,
      handleClick,
      handleBlur,
      handleMouseLeave,
      mobileHandlers,
      triggerElementBase,
    ],
  );

  const interactions: DropdownInteraction[] = isMobile
    ? ["dismiss"]
    : ["hover", "focus", "dismiss"];

  return (
    <Dropdown
      trigger={triggerElement}
      content={content}
      triggerId={triggerId}
      contentId={contentId}
      open={open}
      onOpenChange={onOpenChange}
      disabled={wasClicked}
      interactions={interactions}
      role="tooltip"
      placement={placement}
      offset={offset}
      maxWidth={maxWidth}
      contentClassName={clsx("nuco-tooltip__content", contentClassName)}
      hoverOptions={
        hoverOptions || {
          delay: {
            open: 500,
            close: 0,
          },
        }
      }
    />
  );
};
